import clsx from 'clsx'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { register } from 'swiper/element/bundle'

import downloadIcon from 'assets/icons/download.svg'
import nextIcon from 'assets/icons/gallery-next.svg'
import prevIcon from 'assets/icons/gallery-prev.svg'
import { downloadAtom } from 'atoms/downloads.atom'
import { Spinner } from 'components/uikit/Spinner'
import { useDownloadFile } from 'hooks/useDownloadFile'

import { MediaImage } from './MediaImage'

// The <swiper-container> / <swiper-slide> web components must be registered before use.
// This used to be done internally by the @wppopen library; register() is idempotent.
register()
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'swiper-container': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        navigation?: string
        pagination?: string
        ['pagination-clickable']?: string
      }
      'swiper-slide': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
    }
  }
}
interface SrcSetItem {
  url?: string
  filename?: string
  type?: string
}
interface GalleryProps {
  srcSet: SrcSetItem[]
  placeholder?: string
  loading?: boolean
}

const pauseVideosInSlide = () => {
  const videos = document.querySelectorAll('video')
  videos.forEach(video => {
    if (!video.paused) {
      video.pause()
    }
  })
}
function videosFirst(a: SrcSetItem, b: SrcSetItem): number {
  return a.type === 'video' && b.type !== 'video' ? -1 : a.type !== 'video' && b.type === 'video' ? 1 : 0
}

export const Gallery = ({ srcSet, placeholder, loading }: GalleryProps) => {
  const downloadFile = useDownloadFile()
  const [videoPlaying, setVideoPlaying] = useState(false)
  const sliderRef = useRef<any>(null)
  const [index, setIndex] = useState(0)
  const [srcList, setSrcList] = useState<SrcSetItem[]>([])
  const [isDownloading] = useAtom(downloadAtom)
  useEffect(() => {
    setSrcList([
      ...(Array.isArray(srcSet)
        ? srcSet?.filter(item => item.url !== placeholder)?.sort((a, b) => videosFirst(a, b))
        : []),
      { url: placeholder, type: 'image', filename: 'cover' },
    ])
  }, [placeholder, srcSet])
  sliderRef?.current?.swiper?.on('slideChange', (event: { activeIndex?: number }) => {
    setIndex(event.activeIndex ?? 0)
  })
  return (
    <div
      className={clsx(
        'w-full flex flex-col gap-[24px] relative',
        videoPlaying && '[&_swiper-container::part(pagination)]:hidden justify-center items-center',
      )}
    >
      <img
        src={prevIcon}
        role="button"
        alt="Previous"
        id="swiper-prev"
        className={clsx(
          index === 0 && 'opacity-30 cursor-not-allowed',
          'absolute -left-14 top-[50%] translate-y-[-50%] z-10',
          loading && 'invisible pointer-events-none',
          !loading && index > 0 && 'cursor-pointer',
        )}
        onClick={() => {
          if (!loading && index > 0) {
            pauseVideosInSlide()
            sliderRef.current?.swiper?.slidePrev?.()
          }
        }}
      />
      <img
        src={nextIcon}
        id="swiper-next"
        role="button"
        alt="Next"
        className={clsx(
          index === srcList?.length - 1 && 'opacity-30 cursor-not-allowed',
          'absolute -right-14 top-[50%] translate-y-[-50%] z-10',
          loading && 'invisible pointer-events-none',
          !loading && index < srcList?.length - 1 && 'cursor-pointer',
        )}
        onClick={() => {
          if (!loading && index < srcList?.length - 1) {
            pauseVideosInSlide()
            sliderRef.current?.swiper?.slideNext?.()
          }
        }}
      />
      {!loading && !videoPlaying && (
        <button
          className={`absolute bottom-4 right-6 bg-black w-14 h-10 rounded-[40px] flex justify-center items-center z-10 ${isDownloading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          disabled={isDownloading}
          onClick={() => {
            const attachment = srcList[sliderRef.current?.swiper?.activeIndex]
            attachment.url && downloadFile(attachment.url, attachment.filename ?? 'download')
          }}
        >
          {isDownloading ? (
            <Spinner size="s" className="" color="gray" />
          ) : (
            <img src={downloadIcon} alt="dlIcon" className="w-[15px] h-[15px]" />
          )}
        </button>
      )}
      {loading ? (
        <div className="w-full h-[415px] bg-grey">
          <Spinner size="l" className="absolute z-20 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" color="gray" />
        </div>
      ) : (
        <swiper-container
          navigation={srcList?.length > 1 ? 'true' : undefined}
          pagination="true"
          ref={sliderRef}
          pagination-clickable="true"
        >
          {srcList?.map((media, index) => (
            <swiper-slide key={`slide-${index}`}>
              <MediaImage
                className="rounded-2xl"
                media={media}
                onPauseVideo={() => setVideoPlaying(false)}
                onPlayVideo={() => setVideoPlaying(true)}
                onError={() => setSrcList(prev => prev.filter(item => item.url !== media.url))}
              />
            </swiper-slide>
          ))}
        </swiper-container>
      )}
    </div>
  )
}
